/**
 * Play / Recall / Shuffle / Exchange / Pass. Buttons use `aria-disabled`
 * (not `disabled`) so the disabled reason stays reachable by hover and focus
 * through the tooltip (UX-SPEC §4 "Action bar", §5 tooltips).
 */
import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import { explainDisabled, passTurn, submitDraft } from '../game/actions';
import { recallAllTiles, shuffleRack } from '../game/interactions';
import {
  exchangeState,
  passState,
  passWarning,
  recallState,
  shuffleState,
  submitState,
  type ActionContext,
  type ActionState,
} from '../game/selectors';
import { useUiStore } from '../state/uiStore';
import { ConfirmPopover } from './ConfirmDialog';
import { IconExchange, IconPass, IconPlay, IconRecall, IconShuffle } from './Icons';
import { Tooltip } from './Tooltip';
import './ActionBar.css';

interface ActionBarProps {
  ctx: ActionContext;
  consecutivePasses: number;
  passLimit: number;
}

interface ActionButtonProps {
  label: string;
  state: ActionState;
  tip: string;
  shortcut?: string;
  icon: ReactNode;
  onClick: () => void;
  className?: string;
  buttonRef?: Ref<HTMLButtonElement>;
  children?: ReactNode;
  expanded?: boolean;
}

function ActionButton({ label, state, tip, shortcut, icon, onClick, className, buttonRef, children, expanded }: ActionButtonProps) {
  return (
    <Tooltip content={state.reason ?? tip} shortcut={state.disabled ? undefined : shortcut}>
      <button
        ref={buttonRef}
        type="button"
        className={clsx('btn action', className)}
        aria-disabled={state.disabled || undefined}
        aria-label={label}
        aria-haspopup={expanded !== undefined ? 'dialog' : undefined}
        aria-expanded={expanded}
        onClick={() => {
          // Pressing a disabled action says why (the tooltip alone is easy to
          // miss, and silent with sound off) and rings an invalid move's squares.
          if (state.disabled) {
            explainDisabled(state.reason ?? tip, state.cells, state.invalidMove ? 'error' : 'info');
            return;
          }
          onClick();
        }}
      >
        {icon}
        {children ?? <span className="action__label">{label}</span>}
      </button>
    </Tooltip>
  );
}

export function ActionBar({ ctx, consecutivePasses, passLimit }: ActionBarProps) {
  const dialog = useUiStore((s) => s.dialog);
  const openDialog = useUiStore((s) => s.openDialog);
  const closeDialog = useUiStore((s) => s.closeDialog);
  const passRef = useRef<HTMLButtonElement>(null);
  const [passing, setPassing] = useState(false);

  const submit = submitState(ctx);
  const recall = recallState(ctx);
  const shuffle = shuffleState(ctx);
  const exchange = exchangeState(ctx);
  const pass = passState(ctx);
  const warning = passWarning(consecutivePasses, passLimit);

  // The turn ended under an open confirm (timeout, opponent claimed…): close it.
  useEffect(() => {
    if (dialog === 'pass' && pass.disabled && !passing) closeDialog();
  }, [dialog, pass.disabled, passing, closeDialog]);

  const confirmPass = async () => {
    if (passing) return;
    setPassing(true);
    const ok = await passTurn();
    setPassing(false);
    if (ok) closeDialog();
  };

  return (
    <div className="action-bar" role="toolbar" aria-label="Turn actions">
      <ActionButton
        label="Recall"
        tip="Return all tiles from the board"
        shortcut="Esc"
        icon={<IconRecall />}
        state={recall}
        className="action--secondary"
        onClick={recallAllTiles}
      />
      <ActionButton
        label="Shuffle"
        tip="Shuffle your rack"
        shortcut="Shift S"
        icon={<IconShuffle />}
        state={shuffle}
        className="action--secondary"
        onClick={shuffleRack}
      />
      <ActionButton
        label="Exchange"
        tip="Swap tiles with the bag (uses your turn)"
        shortcut="Shift E"
        icon={<IconExchange />}
        state={exchange}
        className="action--secondary"
        expanded={dialog === 'exchange'}
        onClick={() => openDialog('exchange')}
      />
      <ActionButton
        label="Pass"
        tip="Skip your turn"
        shortcut="Shift P"
        icon={<IconPass />}
        state={pass}
        className="action--secondary"
        buttonRef={passRef}
        expanded={dialog === 'pass'}
        onClick={() => (dialog === 'pass' ? closeDialog() : openDialog('pass'))}
      />
      <span className="action-bar__spacer" aria-hidden="true" />
      <ActionButton
        label={submit.label}
        tip={submit.points != null ? `Play for ${submit.points} points` : 'Play your word'}
        shortcut="Enter"
        icon={ctx.pending ? <span className="btn__spinner" aria-hidden="true" /> : <IconPlay />}
        state={submit}
        className={clsx('btn--primary action--play', ctx.pending && 'is-pending')}
        onClick={() => void submitDraft()}
      >
        <span className="action__play-label">
          Play
          {submit.points != null && (
            <>
              <span className="action__dot" aria-hidden="true">
                ·
              </span>
              <span className="action__points tabular">{submit.points}</span>
            </>
          )}
        </span>
      </ActionButton>

      <ConfirmPopover
        open={dialog === 'pass' && (!pass.disabled || passing)}
        anchorRef={passRef}
        title="Pass your turn?"
        body={
          <>
            <p>You won’t score this turn and keep your tiles.</p>
            {warning && <p className="action-bar__warning">{warning}</p>}
          </>
        }
        confirmLabel="Pass"
        busy={passing}
        onConfirm={() => void confirmPass()}
        onCancel={closeDialog}
      />
    </div>
  );
}
